/**
 * DOM manipulation utilities for injection scripts
 */

import { DEFAULT_TIMEOUT } from "@/constants";
import type { DOMHelpers } from "@/types";

/**
 * Creates an HTML element with optional properties
 */
export const createElement: DOMHelpers["createElement"] = <
  K extends keyof HTMLElementTagNameMap,
>(
  tag: K,
  options?: Partial<HTMLElementTagNameMap[K]>,
): HTMLElementTagNameMap[K] => {
  const element = document.createElement(tag);

  if (options) {
    Object.assign(element, options);
  }

  return element;
};

/**
 * Injects CSS styles into the document head
 */
export const injectStyles = (css: string): HTMLStyleElement => {
  const style = createElement("style", { textContent: css });
  document.head.appendChild(style);
  return style;
};

/**
 * Waits for an element to appear in the DOM
 */
export const waitForElement = (
  selector: string,
  timeout = DEFAULT_TIMEOUT,
): Promise<Element | null> => {
  return new Promise((resolve) => {
    const element = document.querySelector(selector);

    if (element) {
      resolve(element);
      return;
    }

    const observer = new MutationObserver(() => {
      const element = document.querySelector(selector);
      if (element) {
        observer.disconnect();
        resolve(element);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    setTimeout(() => {
      observer.disconnect();
      resolve(null);
    }, timeout);
  });
};

/**
 * Safely executes a function when DOM is ready
 */
export const onDOMReady = (callback: () => void): void => {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback);
  } else {
    callback();
  }
};
